import { useContext, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { AppContext } from 'core/context/app.context';
import { FeatureContext } from 'core/context/feature.context';
import { Feature } from 'core/enums/features';
import { usePlatform } from 'core/hooks/platform';
import { FeatureStore } from 'core/stores/feature.store';
import { Util } from 'utils/common.utils';
import { AccountStore } from '../../../account/account.store';
import { Button } from '../../buttons/button';
import { ButtonType, CircleButtonSize } from '../../buttons/button-types';
import { CircleActionButton } from '../../buttons/circle-action-button/circle-action-button';
import { PopupStore } from '../popup.store';

export const AccountDetailsPopup = observer<{
  popupStore: PopupStore;
  customerNumber: string;
  country: string;
  accountNumber: number;
  styles: { [key: string]: string };
}>(({ popupStore, customerNumber, country, accountNumber, styles }) => {
  const navigate = useNavigate();
  const accountStore = useLocalObservable(() => new AccountStore());
  const appContext = useContext(AppContext);
  const featureContext = useContext(FeatureContext);
  const path = appContext.locale === 'en-za' ? 'help' : 'pay';
  const discoverUrl = `${Util.BaseUrl}/${appContext.locale}/${path}/how-to-pay`;

  const { t } = useTranslation();
  const { translate } = usePlatform();

  useEffect(() => {
    accountStore.initialize(
      customerNumber,
      country,
      accountNumber,
      FeatureStore.getFeaturePermissions(Feature.ViewBalance, featureContext.permissionMap)
    );
  }, [accountStore, customerNumber, country, accountNumber, featureContext]);

  const handleMethodOfPaymentClick = () => {
    navigate('/account/update-payment-method');
  };

  return (
    <>
      <div id="popupContent">
        <div className={`text-regular ${styles.accountDetailsContent}`}>
          <span className={`cust-mt-2 ${styles.textLarge}`}>
            <span className={styles.textBold}>{t('common.accountNo')}</span>
            {accountStore.accountNumber}
          </span>
          <div className={` ${styles.lineItem}`}>
            <span className={styles.textBold}>{t('common.accountType')}</span>
            <span>{accountStore.accountType}</span>
          </div>
          {accountStore.statusActive && (
            <div className={`cust-mt-2 ${styles.lineItem}`}>
              <span className={styles.textBold}>{t('common.status')}</span>
              <span>{accountStore.statusActive}</span>
            </div>
          )}
          {accountStore.billingDate && (
            <div className={`cust-mt-2 ${styles.lineItem}`}>
              <span className={styles.textBold}>{t('common.billingDate')}</span>
              <div>
                <Trans
                  i18nKey="common.ordinals.ordinal"
                  components={{ sup: <sup></sup> }}
                  count={accountStore.billingDate}
                  tOptions={{ ordinal: true }}
                />
              </div>
            </div>
          )}
          <div className={`cust-mt-2 ${styles.lineItem}`}>
            <span className={styles.textBold}>{t('account.methodOfPayment')}</span>
            <div className="d-flex align-items-center">
              {accountStore.readableMOP}
              {accountStore.statusActive === 'Active' && (
                <CircleActionButton
                  meta={{
                    type: ButtonType.Primary,
                    size: CircleButtonSize.Small,
                    title: t('buttons.edit'),
                    name: 'edit',
                    iconName: 'icon-pencil',
                    areaName: 'Account details',
                    actionName: 'Change Payment Method',
                  }}
                  classes={[`display-inline`, `ms-1`, `${styles.pencilCircle}`]}
                  onClick={handleMethodOfPaymentClick}
                ></CircleActionButton>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={`${styles.popupAccountActionButtons}`}>
        {popupStore.config.hasCancelButton && (
          <Button
            classes={[styles.popupButtonSmall, styles.reducePadding]}
            meta={{
              title: t('buttons.discoverWaysToPay'),
              name: 'Discover ways to pay',
              type: ButtonType.OutlineDark,
              areaName: 'myaccount_dashboad_account_details',
            }}
            onClick={() => window.open(discoverUrl, '_blank')}
          ></Button>
        )}
        {popupStore.config.actionButton && (
          <Button
            classes={[styles.popupButtonSmall, styles.reducePadding]}
            meta={popupStore.config.actionButton}
            onClick={() => popupStore.defaultClose()}
          ></Button>
        )}
      </div>

      <div className={styles.note}>
        <div className="text-caption-title-small">
          <p>{translate('account.popupFooter')}</p>
        </div>
      </div>
    </>
  );
});